import { useState } from 'react';
import { api } from '../lib/api';
import type { ContexteConversation, Rattachement } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import { canal, methode, pourcentage } from '../lib/format';
import { Badge, Bandeau, ErreurAffichee, Score, Section, Squelette, Vide } from './ui';
import { BandeauFraicheur } from './Fraicheur';
import { Signaux } from './Signaux';
import { Chiffres, chiffresAffaire } from './Chiffres';
import { FicheClientDepliee } from './FicheClient';
import { BoiteActions } from './Actions';
import type { ContexteFront } from '../lib/front';

/** Onglet « Contexte » : le contexte client de la conversation ouverte. */
export function Contexte({
  cnvId,
  contexteFront,
}: {
  cnvId: string | undefined;
  contexteFront: ContexteFront | undefined;
}) {
  const { donnees, erreur, chargement, rafraichir } = useRequete<ContexteConversation>(
    (signal, forcer) => api.contexte(cnvId as string, signal, forcer),
    [cnvId],
    Boolean(cnvId),
  );

  if (!cnvId) {
    return <Vide>Ouvrez une conversation pour voir le contexte client.</Vide>;
  }
  if (chargement) return <Squelette lignes={6} />;
  if (erreur) return <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />;
  if (!donnees) return <Vide>Aucun contexte pour cette conversation.</Vide>;

  const rattachable = donnees.client !== null;

  return (
    <div className="pile">
      <BandeauFraicheur fraicheur={donnees.fraicheur} />

      <EnTeteConversation contexte={donnees} onRafraichir={rafraichir} />

      {rattachable ? (
        <ContexteRattache
          contexte={donnees}
          contexteFront={contexteFront}
          onFait={rafraichir}
        />
      ) : (
        <ContexteNonRattache
          contexte={donnees}
          contexteFront={contexteFront}
          onFait={rafraichir}
        />
      )}
    </div>
  );
}

function EnTeteConversation({
  contexte,
  onRafraichir,
}: {
  contexte: ContexteConversation;
  onRafraichir: () => void;
}) {
  const { conversation, client, rattachement } = contexte;

  return (
    <header className="entete">
      <div className="entete-tete">
        <h1>{client ? client.groupe : conversation.sujet}</h1>
        <button type="button" className="bouton bouton--discret" onClick={onRafraichir} title="Recharger">
          Recharger
        </button>
      </div>

      {client && <p className="entete-sujet">{conversation.sujet}</p>}

      <div className="entete-meta">
        {client?.site && <span>{client.site}</span>}
        {client && <span>{canal(client.canal)}</span>}
        {client?.integrateur && <span>via {client.integrateur}</span>}
        {conversation.assigne && <span>Assignée à {conversation.assigne}</span>}
        <span>
          {conversation.nb_messages} message{conversation.nb_messages > 1 ? 's' : ''}
          {conversation.nb_commentaires > 0 && ` · ${conversation.nb_commentaires} commentaire${
            conversation.nb_commentaires > 1 ? 's' : ''
          }`}
        </span>
      </div>

      <PastilleRattachement rattachement={rattachement} />
    </header>
  );
}

/** Le rattachement est une information, pas un détail technique : il dit au
 *  commercial à quel point il peut se fier au bilan affiché. */
function PastilleRattachement({ rattachement }: { rattachement: Rattachement }) {
  if (rattachement.statut === 'resolu') {
    return (
      <div className="rattachement">
        <Badge ton="succes">Client identifié</Badge>
        <span className="rattachement-detail">
          par {methode(rattachement.methode)}
          {rattachement.projet_ref ? ` · projet ${rattachement.projet_ref}` : ''}
        </span>
      </div>
    );
  }

  if (rattachement.statut === 'probable') {
    return (
      <div className="rattachement">
        <Badge ton="attention">Rattachement probable</Badge>
        <span className="rattachement-detail">
          par {methode(rattachement.methode)} · confiance {pourcentage(rattachement.confiance)}.
          Vérifiez qu’il s’agit bien de ce client avant d’agir.
        </span>
      </div>
    );
  }

  return (
    <div className="rattachement">
      <Badge ton="neutre">Non rattachée</Badge>
    </div>
  );
}

function ContexteRattache({
  contexte,
  contexteFront,
  onFait,
}: {
  contexte: ContexteConversation;
  contexteFront: ContexteFront | undefined;
  onFait: () => void;
}) {
  const [ficheOuverte, setFicheOuverte] = useState(false);
  const client = contexte.client!;
  const groupeId = contexte.rattachement.groupe_id;

  return (
    <>
      {contexte.action_recommandee && (
        <Section titre="Action recommandée" repliable cle="ctx.action">
          <p className="action">{contexte.action_recommandee}</p>
        </Section>
      )}

      {typeof contexte.score_reprise === 'number' && (
        <Score valeur={contexte.score_reprise} />
      )}

      <BoiteActions
        cible={{
          conv_id: contexte.conversation.id,
          tiers_id: contexte.rattachement.tiers_id,
          projet_ref: contexte.rattachement.projet_ref,
          client: client.groupe,
          explications: contexte.explications,
        }}
        contexteFront={contexteFront}
        auteur={contexte.conversation.assigne ?? undefined}
        onFait={onFait}
        compact
      />

      <Section titre="Chiffres" repliable cle="ctx.chiffres">
        <Chiffres elements={chiffresAffaire(client)} />
        <p className="aide">
          {client.nb_projets} projet{client.nb_projets > 1 ? 's' : ''}, dont{' '}
          {client.nb_projets_ouverts} ouvert{client.nb_projets_ouverts > 1 ? 's' : ''}.
        </p>
      </Section>

      {contexte.bilan ? (
        <Section titre="Bilan" repliable cle="ctx.bilan">
          <p className="bilan">{contexte.bilan}</p>
        </Section>
      ) : (
        /*
         * Cas majoritaire au lancement : le contrat annonce 300 extractions sur
         * 330 510 conversations indexées. Le client est bien identifié, mais le
         * pipeline n'a pas encore analysé son dossier. Sans ce bandeau, la
         * section « Bilan » disparaîtrait sans un mot et le commercial lirait
         * l'absence d'analyse comme une absence de sujet.
         */
        <Bandeau ton="info" titre="Bilan pas encore produit">
          Le pipeline n’a pas encore analysé ce dossier. Les chiffres ci-dessus
          viennent de Dolibarr et sont à jour ; l’analyse des échanges viendra.
        </Bandeau>
      )}

      {contexte.signaux.length > 0 && (
        <Section titre="Signaux" repliable cle="ctx.signaux" ouvertParDefaut={false}>
          <Signaux signaux={contexte.signaux} />
        </Section>
      )}

      {/* `groupe_id` vient du pipeline. Il n'est jamais reconstruit ici. */}
      {groupeId && (
        <Section>
          <button
            type="button"
            className="bouton bouton--large"
            aria-expanded={ficheOuverte}
            onClick={() => setFicheOuverte((ouvert) => !ouvert)}
          >
            {ficheOuverte ? 'Masquer la fiche client' : 'Voir toute la fiche client'}
          </button>
          {ficheOuverte && <FicheClientDepliee groupeId={groupeId} />}
        </Section>
      )}
    </>
  );
}

/**
 * Rattachement inconnu : cas nominal, environ un tiers des conversations.
 * Leads du formulaire du site, fils portés par un fabricant. On l'annonce
 * comme un état du dossier, jamais comme une erreur, et on affiche ce que le
 * pipeline sait quand même — les signaux sont souvent renseignés.
 */
function ContexteNonRattache({
  contexte,
  contexteFront,
  onFait,
}: {
  contexte: ContexteConversation;
  contexteFront: ContexteFront | undefined;
  onFait: () => void;
}) {
  const { rattachement } = contexte;

  return (
    <>
      <Bandeau ton="info" titre="Conversation non rattachée à un client">
        Le pipeline n’a pas pu relier cette conversation à un dossier Dolibarr.
        C’est fréquent : leads du formulaire du site, fils portés par un fabricant.
        {rattachement.methode ? ` Dernière piste explorée : ${methode(rattachement.methode)}.` : ''}
      </Bandeau>

      {contexte.signaux.length > 0 && (
        <Section titre="Ce que le pipeline a repéré" repliable cle="ctx.repere"
               ouvertParDefaut={false}>
          <Signaux signaux={contexte.signaux} />
        </Section>
      )}

      <BoiteActions
        cible={{
          conv_id: contexte.conversation.id,
          client: contexte.conversation.interlocuteur,
          explications: contexte.explications,
        }}
        contexteFront={contexteFront}
        auteur={contexte.conversation.assigne ?? undefined}
        onFait={onFait}
        compact
      />

      <p className="aide">
        Pour rattacher le dossier, renseignez la référence de projet côté Dolibarr,
        ou dites-le ici : « dans Dolibarr elle s’appelle X ».
      </p>
    </>
  );
}
